import React, { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { authService } from '@/services/authService'
import api from '@/services/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Lock, Loader2, CheckCircle, XCircle, ArrowLeft, ShieldCheck, KeyRound,
    UserPlus, Users, Mail, User, X, Search,
} from 'lucide-react'

function Field({ label, value }: { label: string; value?: string | null }) {
    const displayValue = value || '—'
    const isEmpty = !value
    return (
        <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                {label}
            </label>
            <input
                readOnly
                value={displayValue}
                className={`w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-sm font-semibold outline-none cursor-default ${isEmpty ? 'text-slate-400' : 'text-slate-700'}`}
            />
        </div>
    )
}

interface AgencyUser {
    id: number
    email: string
    first_name: string
    last_name: string
    is_primary_contact: boolean
    email_verified: boolean
    is_login_enabled: boolean
    created_at: string | null
}

interface CreateUserForm {
    first_name: string
    last_name: string
    email: string
    password: string
}

export default function AgencyAccountTab() {
    const { user } = useAuth()
    const [showPasswordForm, setShowPasswordForm] = useState(false)

    // Password form state
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(false)

    // Sub-user creation
    const canCreateUsers = user?.enable_subagent_creation && user?.is_primary_contact
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [createForm, setCreateForm] = useState<CreateUserForm>({ first_name: '', last_name: '', email: '', password: '' })
    const [createError, setCreateError] = useState<string | null>(null)
    const [createSuccess, setCreateSuccess] = useState(false)
    const [isCreating, setIsCreating] = useState(false)
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof CreateUserForm, string>>>({})

    // Agency users list
    const [agencyUsers, setAgencyUsers] = useState<AgencyUser[]>([])
    const [usersLoading, setUsersLoading] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')

    useEffect(() => {
        if (!showCreateModal) {
            setFieldErrors({})
        }
    }, [showCreateModal])

    useEffect(() => {
        if (canCreateUsers) {
            loadAgencyUsers()
        }
    }, [canCreateUsers])

    const loadAgencyUsers = async () => {
        setUsersLoading(true)
        try {
            const res = await api.get('/agency/users/')
            setAgencyUsers(res.data.users || [])
        } catch {
            // non-fatal
        } finally {
            setUsersLoading(false)
        }
    }

    const trimmedQuery = searchQuery.trim().toLowerCase()
    const filteredUsers = trimmedQuery
        ? agencyUsers.filter(
            (agencyUser) =>
                `${agencyUser.first_name} ${agencyUser.last_name}`.toLowerCase().includes(trimmedQuery) ||
                agencyUser.email.toLowerCase().includes(trimmedQuery)
          )
        : agencyUsers

    const handlePasswordSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        setError(null)
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.')
            return
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters.')
            return
        }
        setIsSubmitting(true)
        try {
            await authService.changePassword(currentPassword, newPassword, confirmPassword)
            setSuccess(true)
            setCurrentPassword('')
            setNewPassword('')
            setConfirmPassword('')
            setTimeout(() => {
                setShowPasswordForm(false)
                setSuccess(false)
            }, 2000)
        } catch (apiError: any) {
            const detail =
                apiError.response?.data?.detail ||
                apiError.response?.data?.current_password?.[0] ||
                apiError.response?.data?.new_password?.[0] ||
                apiError.response?.data?.confirm_password?.[0] ||
                'Failed to change password. Please try again.'
            setError(Array.isArray(detail) ? detail[0] : detail)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCancel = () => {
        setShowPasswordForm(false)
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setError(null)
        setSuccess(false)
    }

    const handleCreateFormChange = (field: keyof CreateUserForm, value: string) => {
        let sanitized = value
        if (field === 'first_name' || field === 'last_name') {
            sanitized = value.replace(/\d/g, '').slice(0, 40)
        }
        setCreateForm(prev => ({ ...prev, [field]: sanitized }))
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: undefined }))
        }
    }

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault()
        setCreateError(null)

        const errors: Partial<Record<keyof CreateUserForm, string>> = {}
        if (!createForm.first_name.trim()) errors.first_name = 'First Name is required.'
        if (!createForm.last_name.trim()) errors.last_name = 'Last Name is required.'
        if (!createForm.email.trim()) {
            errors.email = 'Email is required.'
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email)) {
            errors.email = 'Please enter a valid email address.'
        }
        if (!createForm.password) {
            errors.password = 'Password is required.'
        } else if (createForm.password.length < 8) {
            errors.password = 'Password must be at least 8 characters.'
        } else if (!/\d/.test(createForm.password)) {
            errors.password = 'Password must contain at least one number.'
        }
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)
            return
        }

        setIsCreating(true)
        try {
            await api.post('/agency/users/create/', createForm)
            setCreateSuccess(true)
            setCreateForm({ first_name: '', last_name: '', email: '', password: '' })
            await loadAgencyUsers()
            setTimeout(() => {
                setShowCreateModal(false)
                setCreateSuccess(false)
            }, 2000)
        } catch (err: any) {
            const code = err.response?.data?.error
            const msgMap: Record<string, string> = {
                already_registered: 'A user with this email already exists.',
                email_required: 'Email is required.',
                password_required: 'Password is required.',
                name_required: 'First and last name are required.',
                password_too_short: 'Password must be at least 8 characters.',
                password_no_digit: 'Password must contain at least one number.',
                not_primary_contact: 'Only the primary contact can create users.',
                subagent_creation_disabled: 'Sub-user creation is not enabled for your agency.',
            }
            setCreateError(msgMap[code] || 'Failed to create user. Please try again.')
        } finally {
            setIsCreating(false)
        }
    }

    const closeModal = () => {
        setShowCreateModal(false)
        setCreateForm({ first_name: '', last_name: '', email: '', password: '' })
        setCreateError(null)
        setCreateSuccess(false)
    }

    if (showPasswordForm) {
        return (
            <div className="w-full min-h-[50vh] flex items-center justify-center px-4 pb-10">
                <div className="w-full max-w-sm">
                    {success ? (
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm text-center">
                            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-500 flex items-center justify-center">
                                <CheckCircle className="w-8 h-8 text-white" />
                            </div>
                            <h2 className="text-xl font-bold text-slate-800 mb-2">Password Changed!</h2>
                            <p className="text-sm text-slate-500">Your password has been updated successfully.</p>
                        </div>
                    ) : (
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm">
                            <button
                                onClick={handleCancel}
                                className="mb-5 flex min-h-[44px] min-w-[44px] items-center gap-2 px-3 py-2 text-sm text-slate-500 active:text-slate-700"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back
                            </button>
                            <div className="text-center mb-6">
                                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#001B44] flex items-center justify-center">
                                    <KeyRound className="w-7 h-7 text-white" />
                                </div>
                                <h2 className="text-lg font-bold text-slate-800">Change Password</h2>
                                <p className="text-sm text-slate-500 mt-1">Enter your new password</p>
                            </div>
                            {error && (
                                <div className="flex items-start gap-3 p-3 mb-5 bg-red-50 text-red-700 rounded-xl border border-red-100">
                                    <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <span className="text-sm">{error}</span>
                                </div>
                            )}
                            <form onSubmit={handlePasswordSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Current Password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            type="password"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            placeholder="Current password"
                                            className="w-full pl-11 pr-4 py-3.5 h-auto rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">New Password</label>
                                    <div className="relative">
                                        <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="New password"
                                            className="w-full pl-11 pr-4 py-3.5 h-auto rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                            required
                                            minLength={8}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Confirm Password</label>
                                    <div className="relative">
                                        <ShieldCheck className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Confirm password"
                                            className="w-full pl-11 pr-4 py-3.5 h-auto rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                            required
                                            minLength={8}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 pt-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleCancel}
                                        className="w-full sm:flex-1 h-12 rounded-xl font-semibold border-slate-200"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full sm:flex-1 h-12 rounded-xl font-semibold bg-[#001B44]"
                                    >
                                        {isSubmitting ? (
                                            <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Saving...</>
                                        ) : (
                                            'Update Password'
                                        )}
                                    </Button>
                                </div>
                                <p className="text-xs text-center text-slate-400 pt-1">
                                    Min 8 characters with at least one number
                                </p>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="w-full space-y-6 pb-10">
            {/* Account Info Card */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
                <div className="mb-6">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Account</h3>
                    <div className="h-1 w-10 bg-[#001B44] rounded-full" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Account Name" value={user?.agency_name} />
                    <Field label="Email" value={user?.email} />
                </div>
                <div className="mt-6 pt-6 border-t border-slate-100 flex flex-wrap items-center gap-3">
                    <Button
                        onClick={() => setShowPasswordForm(true)}
                        variant="outline"
                        className="h-11 px-6 rounded-2xl font-semibold border-slate-200 hover:bg-slate-50"
                    >
                        <Lock className="w-4 h-4 mr-2" />
                        Reset Password
                    </Button>
                </div>
            </div>

            {/* Create New User section — only visible to primary contact with subagent creation enabled */}
            {canCreateUsers && (
                <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
                    <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Create New User</h3>
                            <div className="h-1 w-10 bg-[#001B44] rounded-full" />
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                                <Input
                                    type="search"
                                    aria-label="Search agency users"
                                    placeholder="Search by name or email"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9 h-11 w-64 rounded-2xl border-slate-400 text-sm"
                                />
                            </div>
                            <Button
                                onClick={() => setShowCreateModal(true)}
                                className="h-11 px-6 rounded-2xl font-semibold bg-[#001B44] hover:bg-[#002a6e]"
                            >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Create New User
                            </Button>
                        </div>
                    </div>

                    {/* Agency users table */}
                    {usersLoading ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                    ) : agencyUsers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                            <Users className="w-10 h-10 mb-3 opacity-40" />
                            <p className="text-sm font-medium">No users yet</p>
                            <p className="text-xs mt-1">Create the first user for your agency.</p>
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                            <Search className="w-10 h-10 mb-3 opacity-40" />
                            <p className="text-sm font-medium">No users match your search</p>
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="mt-2 min-h-[44px] px-3 text-xs text-[#001B44] font-semibold hover:underline"
                            >
                                Clear search
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-slate-100">
                                            <th className="text-left text-[10px] font-black uppercase tracking-widest text-slate-400 pb-3 pr-4">Name</th>
                                            <th className="text-left text-[10px] font-black uppercase tracking-widest text-slate-400 pb-3 pr-4">Email</th>
                                            <th className="text-left text-[10px] font-black uppercase tracking-widest text-slate-400 pb-3 pr-4">Role</th>
                                            <th className="text-left text-[10px] font-black uppercase tracking-widest text-slate-400 pb-3">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {filteredUsers.map((agencyUser) => (
                                            <tr key={agencyUser.id} className="hover:bg-slate-50 transition-colors">
                                                <td className="py-3.5 pr-4 font-semibold text-slate-700">
                                                    {agencyUser.first_name} {agencyUser.last_name}
                                                </td>
                                                <td className="py-3.5 pr-4 text-slate-500">{agencyUser.email}</td>
                                                <td className="py-3.5 pr-4">
                                                    {agencyUser.is_primary_contact ? (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#001B44]/10 text-[#001B44]">
                                                            Primary Contact
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500">
                                                            User
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-3.5">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${agencyUser.email_verified ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                                                        {agencyUser.email_verified ? 'Verified' : 'Pending'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="md:hidden space-y-3">
                                {filteredUsers.map((agencyUser) => (
                                    <div key={agencyUser.id} className="border border-slate-100 rounded-2xl p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <p className="font-semibold text-slate-700 text-sm">{agencyUser.first_name} {agencyUser.last_name}</p>
                                                <p className="text-xs text-slate-400 mt-0.5">{agencyUser.email}</p>
                                            </div>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${agencyUser.email_verified ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                                                {agencyUser.email_verified ? 'Verified' : 'Pending'}
                                            </span>
                                        </div>
                                        {agencyUser.is_primary_contact && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#001B44]/10 text-[#001B44]">
                                                Primary Contact
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Create User Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeModal} />
                    <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 sm:p-8">
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        {createSuccess ? (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-500 flex items-center justify-center">
                                    <CheckCircle className="w-8 h-8 text-white" />
                                </div>
                                <h2 className="text-xl font-bold text-slate-800 mb-2">User Created!</h2>
                                <p className="text-sm text-slate-500">The new user has been added to your agency.</p>
                            </div>
                        ) : (
                            <>
                                <div className="text-center mb-6">
                                    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#001B44] flex items-center justify-center">
                                        <UserPlus className="w-7 h-7 text-white" />
                                    </div>
                                    <h2 className="text-lg font-bold text-slate-800">Create New User</h2>
                                    <p className="text-sm text-slate-500 mt-1">Add a new user to your agency</p>
                                </div>

                                {createError && (
                                    <div className="flex items-start gap-3 p-3 mb-5 bg-red-50 text-red-700 rounded-xl border border-red-100">
                                        <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                        <span className="text-sm">{createError}</span>
                                    </div>
                                )}

                                <form onSubmit={handleCreateUser} className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label htmlFor="create-first-name" className="block text-xs font-semibold text-slate-600 mb-1.5">
                                                First Name <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                <Input
                                                    id="create-first-name"
                                                    type="text"
                                                    value={createForm.first_name}
                                                    onChange={(e) => handleCreateFormChange('first_name', e.target.value)}
                                                    placeholder="First name"
                                                    maxLength={40}
                                                    aria-invalid={!!fieldErrors.first_name}
                                                    aria-describedby={fieldErrors.first_name ? 'error-first-name' : undefined}
                                                    className={`w-full pl-9 pr-3 py-3 h-auto rounded-xl border bg-slate-50 text-sm ${fieldErrors.first_name ? 'border-red-400' : 'border-slate-200'}`}
                                                />
                                            </div>
                                            {fieldErrors.first_name && <p id="error-first-name" className="mt-1 text-xs text-red-500">{fieldErrors.first_name}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="create-last-name" className="block text-xs font-semibold text-slate-600 mb-1.5">
                                                Last Name <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                <Input
                                                    id="create-last-name"
                                                    type="text"
                                                    value={createForm.last_name}
                                                    onChange={(e) => handleCreateFormChange('last_name', e.target.value)}
                                                    placeholder="Last name"
                                                    maxLength={40}
                                                    aria-invalid={!!fieldErrors.last_name}
                                                    aria-describedby={fieldErrors.last_name ? 'error-last-name' : undefined}
                                                    className={`w-full pl-9 pr-3 py-3 h-auto rounded-xl border bg-slate-50 text-sm ${fieldErrors.last_name ? 'border-red-400' : 'border-slate-200'}`}
                                                />
                                            </div>
                                            {fieldErrors.last_name && <p id="error-last-name" className="mt-1 text-xs text-red-500">{fieldErrors.last_name}</p>}
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="create-email" className="block text-xs font-semibold text-slate-600 mb-1.5">
                                            Email <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <Input
                                                id="create-email"
                                                type="email"
                                                value={createForm.email}
                                                onChange={(e) => handleCreateFormChange('email', e.target.value)}
                                                placeholder="email@example.com"
                                                aria-invalid={!!fieldErrors.email}
                                                aria-describedby={fieldErrors.email ? 'error-email' : undefined}
                                                className={`w-full pl-10 pr-4 py-3 h-auto rounded-xl border bg-slate-50 text-sm ${fieldErrors.email ? 'border-red-400' : 'border-slate-200'}`}
                                            />
                                        </div>
                                        {fieldErrors.email && <p id="error-email" className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
                                    </div>
                                    <div>
                                        <label htmlFor="create-password" className="block text-xs font-semibold text-slate-600 mb-1.5">
                                            Password <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <Input
                                                id="create-password"
                                                type="password"
                                                value={createForm.password}
                                                onChange={(e) => handleCreateFormChange('password', e.target.value)}
                                                placeholder="Min 8 characters, 1 number"
                                                aria-invalid={!!fieldErrors.password}
                                                aria-describedby={fieldErrors.password ? 'error-password' : undefined}
                                                className={`w-full pl-10 pr-4 py-3 h-auto rounded-xl border bg-slate-50 text-sm ${fieldErrors.password ? 'border-red-400' : 'border-slate-200'}`}
                                            />
                                        </div>
                                        {fieldErrors.password && <p id="error-password" className="mt-1 text-xs text-red-500">{fieldErrors.password}</p>}
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={closeModal}
                                            className="w-full sm:flex-1 h-12 rounded-xl font-semibold border-slate-200"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            disabled={isCreating}
                                            className="w-full sm:flex-1 h-12 rounded-xl font-semibold bg-[#001B44] hover:bg-[#002a6e]"
                                        >
                                            {isCreating ? (
                                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                                            ) : (
                                                'Create User'
                                            )}
                                        </Button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
